import { useTranslation } from 'react-i18next'
import { Shield, FileCheck, Scale } from 'lucide-react'

interface LoginPromptProps {
  onLogin: () => void
  isLoggingIn: boolean
  error: string | null
}

/**
 * Premium branded login screen.
 * Gradient background, KobSign logo, trust badges, staggered animations.
 */
export function LoginPrompt({ onLogin, isLoggingIn, error }: LoginPromptProps) {
  const { t } = useTranslation()

  return (
    <div className="relative flex flex-col items-center justify-center flex-1 px-6 py-8 text-center overflow-hidden bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-950 dark:via-neutral-900 dark:to-[rgb(var(--color-surface))]">
      {/* Decorative background circles */}
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-primary-200/50 dark:bg-primary-800/30 blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-accent-200/40 dark:bg-accent-800/20 blur-3xl" />
      {/* Logo + branding */}
      <div className="animate-fade-in relative z-10">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 flex items-center justify-center mb-3 shadow-lg shadow-primary-500/20 dark:shadow-primary-900/30">
          <span className="text-xl font-extrabold tracking-tight text-white">
            S
          </span>
        </div>
        <div className="mb-1">
          <span className="text-2xl font-extrabold tracking-tight text-primary-600 dark:text-primary-400">
            {t('auth.brandName', 'sign')}
          </span>
        </div>
        <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mb-6">
          {t('auth.brandSubtitle', 'by kobsign')}
        </p>
      </div>

      {/* Headline */}
      <div className="animate-fade-in relative z-10" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
        <h1 className="text-[17px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-1.5">
          {t('auth.loginTitle')}
        </h1>
        <p className="text-[13px] text-neutral-500 dark:text-neutral-400 mb-8 max-w-[240px] leading-relaxed">
          {t('auth.loginSubtitle')}
        </p>
      </div>

      {/* Sign in button */}
      <div className="w-full animate-fade-in relative z-10" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
        <button
          onClick={onLogin}
          disabled={isLoggingIn}
          className="w-full px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
        >
          {isLoggingIn ? t('auth.loggingIn') : t('auth.loginButton')}
        </button>
      </div>

      {error && (
        <p className="mt-3 text-xs text-error-500 animate-fade-in">
          {t(error)}
        </p>
      )}

      {/* Trust badges */}
      <div
        className="mt-8 flex flex-col gap-2.5 w-full animate-fade-in relative z-10"
        style={{ animationDelay: '400ms', animationFillMode: 'both' }}
      >
        <TrustBadge icon={Shield} text={t('auth.trustBadgeSecurity')} />
        <TrustBadge icon={FileCheck} text={t('auth.trustBadgeSignatures')} />
        <TrustBadge icon={Scale} text={t('auth.trustBadgeCompliance')} />
      </div>
    </div>
  )
}

function TrustBadge({ icon: Icon, text }: { icon: typeof Shield; text: string }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white dark:bg-neutral-800 border border-primary-200 dark:border-primary-700/50 shadow-sm">
      <div className="w-6 h-6 rounded-md bg-primary-100 dark:bg-primary-800/50 flex items-center justify-center shrink-0">
        <Icon size={13} className="text-primary-600 dark:text-primary-300" />
      </div>
      <span className="text-[11px] font-medium text-neutral-700 dark:text-neutral-300">
        {text}
      </span>
    </div>
  )
}
